import type {
	ApprovalActivityAction,
	ApprovalActivityAnalyticsAttributes,
	ApprovalActivityDisplayStatus,
	ApprovalActivityHistory,
	ApprovalActivityItem,
	ApprovalActivityKnownAction,
	ConfluencePrincipalType,
	ConfluenceReviewerDecisionState,
} from './approvalActivityTypes.ts';
import type { ApprovalActivityHistoryQuery$data } from './__generated__/ApprovalActivityHistoryQuery.graphql.ts';

type NullableString = string | null | undefined;

type HistoryConnection = NonNullable<
	ApprovalActivityHistoryQuery$data['confluence_workflowApplicationHistory']
>;
type HistoryEdge = NonNullable<NonNullable<HistoryConnection['edges']>[number]>;
type HistoryNode = NonNullable<HistoryEdge['node']>;
type WorkflowStatusForDisplay = {
	id?: NullableString;
	allowsReviews?: boolean | null;
	name?: NullableString;
};

type ActivityActorWithHydratedUser = NonNullable<HistoryNode['actor']> & {
	user?: {
		photos?: ReadonlyArray<{ value?: NullableString } | null> | null;
	} | null;
};

const getActorAvatarSrc = (actor: HistoryNode['actor']): string | null => {
	const hydratedActor = actor as ActivityActorWithHydratedUser | null | undefined;
	return hydratedActor?.user?.photos?.find((photo) => photo?.value)?.value ?? null;
};

// `action` is a backend String, not an enum; unknown/future values map to
// `UNKNOWN` so new backend events don't break rendering.
const KNOWN_ACTIONS: ReadonlySet<string> = new Set<ApprovalActivityKnownAction>([
	'REVIEW_STARTED',
	'REVIEW_REAPPROVAL_STARTED',
	'REVIEW_APPROVED',
	'REVIEW_DENIED',
	'REVIEW_CANCELED',
	'REVIEW_INVALIDATED',
	'DRAFT_PROMOTED',
	'REVIEWER_ADDED',
	'REVIEWER_REMOVED',
	'REVIEWER_REQUIRED_CHANGED',
	'REVIEWER_DECISION_SUBMITTED',
	'REVIEWER_DECISION_UPDATED',
]);

const ACTION_DISPLAY_STATUS: Record<ApprovalActivityKnownAction, ApprovalActivityDisplayStatus> = {
	REVIEW_STARTED: 'IN_REVIEW',
	REVIEW_REAPPROVAL_STARTED: 'IN_REVIEW',
	REVIEW_APPROVED: 'APPROVED',
	REVIEW_DENIED: 'CHANGES_REQUESTED',
	REVIEW_CANCELED: 'CANCELLED',
	REVIEW_INVALIDATED: 'CANCELLED',
	DRAFT_PROMOTED: 'UPDATED',
	REVIEWER_ADDED: 'IN_REVIEW',
	REVIEWER_REMOVED: 'IN_REVIEW',
	REVIEWER_REQUIRED_CHANGED: 'UPDATED',
	REVIEWER_DECISION_SUBMITTED: 'UPDATED',
	REVIEWER_DECISION_UPDATED: 'UPDATED',
};

// Backend-defined workflow-status id for "changes requested". The id is
// locale-independent, unlike the display name, so it is the only reliable
// signal for this state. See the Confluence workflow-status schema.
const CHANGES_REQUESTED_WORKFLOW_STATUS_ID = '3';

export const mapApprovalActivityAction = (action: NullableString): ApprovalActivityAction => {
	if (action && KNOWN_ACTIONS.has(action)) {
		return action as ApprovalActivityKnownAction;
	}
	return 'UNKNOWN';
};

export const mapApprovalActivityDisplayStatus = (
	action: ApprovalActivityAction,
	decisionStatus: NullableString = null,
	workflowStatus: WorkflowStatusForDisplay | null = null,
): ApprovalActivityDisplayStatus => {
	if (action === 'UNKNOWN') {
		return 'UPDATED';
	}

	// A reviewer's decision only carries an approve/deny status; cancellation is a
	// cycle-level action (REVIEW_CANCELED), handled where cycle status is derived.
	if (action === 'REVIEWER_DECISION_SUBMITTED' || action === 'REVIEWER_DECISION_UPDATED') {
		if (decisionStatus === 'REVIEWER_APPROVED') {
			return 'APPROVED';
		}
		if (decisionStatus === 'REVIEWER_DENIED') {
			return 'CHANGES_REQUESTED';
		}
	}

	if (action === 'DRAFT_PROMOTED' && workflowStatus) {
		if (workflowStatus.id === CHANGES_REQUESTED_WORKFLOW_STATUS_ID) {
			return 'CHANGES_REQUESTED';
		}
		if (workflowStatus.allowsReviews) {
			return 'IN_REVIEW';
		}
		return 'APPROVED';
	}

	return ACTION_DISPLAY_STATUS[action];
};

const mapConfluencePrincipalType = (
	principalType: NullableString,
): ConfluencePrincipalType | null => principalType ?? null;

const mapConfluenceReviewerDecisionState = (
	decisionStatus: NullableString,
): ConfluenceReviewerDecisionState | null => decisionStatus ?? null;

const shouldOmitReviewDecisionComment = (action: ApprovalActivityAction): boolean =>
	action === 'REVIEW_CANCELED' || action === 'REVIEW_INVALIDATED';

export const mapApprovalActivityNode = (
	node: HistoryNode | null | undefined,
): ApprovalActivityItem | null => {
	if (!node?.id) {
		return null;
	}

	const action = mapApprovalActivityAction(node.action);
	const workflowStatus = node.workflowStatus;
	const mappedWorkflowStatus = workflowStatus
		? {
				id: workflowStatus.id ?? null,
				allowsReviews: workflowStatus.allowsReviews ?? null,
				name: workflowStatus.displayProperties?.name ?? null,
				description: workflowStatus.displayProperties?.description ?? null,
				color: workflowStatus.displayProperties?.color ?? null,
				icon: workflowStatus.displayProperties?.icon ?? null,
			}
		: null;
	const decisionStatus = node.reviewDecision?.decisionStatus ?? null;

	return {
		id: node.id,
		action,
		rawAction: node.action ?? null,
		displayStatus: mapApprovalActivityDisplayStatus(action, decisionStatus, mappedWorkflowStatus),
		creationDate: node.creationDate?.value ?? null,
		contentStatus: node.contentStatus ?? null,
		contentVersion: node.contentVersion ?? null,
		triggerSource: node.triggerSource ?? null,
		actor: node.actor
			? {
					accountId: node.actor.accountId ?? null,
					displayName: node.actor.displayName ?? null,
					avatarSrc: getActorAvatarSrc(node.actor),
				}
			: null,
		targetPrincipal: node.targetPrincipal
			? {
					principalId: node.targetPrincipal.principalId ?? null,
					principalType: mapConfluencePrincipalType(node.targetPrincipal.principalType),
				}
			: null,
		reviewDecision: node.reviewDecision
			? {
					decisionStatus: mapConfluenceReviewerDecisionState(node.reviewDecision.decisionStatus),
					comment: shouldOmitReviewDecisionComment(action)
						? null
						: (node.reviewDecision.comment ?? null),
				}
			: null,
		workflowStatus: mappedWorkflowStatus,
		workflowApplicationId: node.workflowApplicationId ?? null,
		workflowReviewId: node.workflowReviewId ?? null,
	};
};

export const mapApprovalActivityHistory = (
	data: ApprovalActivityHistoryQuery$data | null | undefined,
): ApprovalActivityHistory => {
	const connection = data?.confluence_workflowApplicationHistory;
	const edges = connection?.edges ?? [];
	const items: ApprovalActivityItem[] = [];

	for (const edge of edges) {
		const item = mapApprovalActivityNode(edge?.node);
		if (item) {
			items.push(item);
		}
	}

	return {
		items,
		pageInfo: {
			endCursor: connection?.pageInfo?.endCursor ?? null,
			hasNextPage: connection?.pageInfo?.hasNextPage ?? false,
		},
	};
};

export const mergeApprovalActivityHistoryQueryData = (
	previousResult: ApprovalActivityHistoryQuery$data,
	fetchMoreResult: ApprovalActivityHistoryQuery$data | null | undefined,
): ApprovalActivityHistoryQuery$data => {
	const previousConnection = previousResult.confluence_workflowApplicationHistory;
	const nextConnection = fetchMoreResult?.confluence_workflowApplicationHistory;

	if (!previousConnection) {
		return fetchMoreResult ?? previousResult;
	}
	if (!nextConnection) {
		return previousResult;
	}

	// Generated type marks cursor as non-nullable, but we guard against falsy
	// cursors defensively (TODO CCIMPORTER-2844: confirm with backend, then drop
	// this guard or fix the schema upstream).
	const seenCursors = new Set<string>();
	const seenNodeIds = new Set<string>();
	for (const edge of previousConnection.edges ?? []) {
		if (edge?.cursor) {
			seenCursors.add(edge.cursor);
		}
		if (edge?.node?.id) {
			seenNodeIds.add(edge.node.id);
		}
	}
	const nextEdges: Array<NonNullable<HistoryConnection['edges']>[number]> = [];
	for (const edge of nextConnection.edges ?? []) {
		const cursor = edge?.cursor;
		const nodeId = edge?.node?.id;
		if (cursor) {
			if (seenCursors.has(cursor)) {
				continue;
			}
			seenCursors.add(cursor);
		}
		if (nodeId) {
			if (seenNodeIds.has(nodeId)) {
				continue;
			}
			seenNodeIds.add(nodeId);
		}
		nextEdges.push(edge);
	}

	return {
		...previousResult,
		confluence_workflowApplicationHistory: {
			...nextConnection,
			edges: [...(previousConnection.edges ?? []), ...nextEdges],
			pageInfo: nextConnection.pageInfo,
		},
	};
};

const getPageSizeBucket = (
	itemCount: number,
): ApprovalActivityAnalyticsAttributes['pageSizeBucket'] => {
	if (itemCount === 0) {
		return 'empty';
	}
	if (itemCount === 1) {
		return 'one';
	}
	if (itemCount < 20) {
		return 'few';
	}
	return 'many';
};

export const getApprovalActivityAnalyticsAttributes = (
	item: ApprovalActivityItem,
	history: ApprovalActivityHistory,
): ApprovalActivityAnalyticsAttributes => {
	return {
		action: item.action,
		displayStatus: item.displayStatus,
		contentStatus: item.contentStatus,
		hasActor: Boolean(item.actor),
		hasComment: Boolean(item.reviewDecision?.comment),
		hasNextPage: history.pageInfo.hasNextPage,
		pageSizeBucket: getPageSizeBucket(history.items.length),
	};
};
